import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface StatItem {
  value: string;
  label: string;
}

export interface FeatureItem {
  icon: string;
  title: string;
  description: string;
}

export interface WhyChooseContent {
  title: string;
  description: string;
  stats: StatItem[];
  features: FeatureItem[];
}

const DEFAULT_WHY_CHOOSE_CONTENT: WhyChooseContent = {
  title: '',
  description:
    'Showcase your key numbers and what makes you the best choice. Edit these from your admin dashboard.',
  stats: [
    { value: '90%', label: 'Of messages replied to within the hour' },
    { value: '100%', label: 'Fully insured and vetted cleaners' },
    { value: '40+', label: 'Checklist points in every standard clean' },
    { value: '5yr+', label: 'Average cleaner experience on our team' },
  ],
  features: [
    { icon: 'Shield', title: 'Fully insured', description: 'Public liability insurance for complete peace of mind.' },
    { icon: 'Clock', title: 'Flexible scheduling', description: 'Morning, afternoon, or evening. We work around your schedule.' },
    { icon: 'Heart', title: 'Same cleaner', description: 'Build a relationship with your regular cleaner who knows your home.' },
    { icon: 'Award', title: 'Quality guaranteed', description: 'Not 100% happy? We will come back and fix it for free.' },
    { icon: 'Leaf', title: 'Eco friendly options', description: 'Non-toxic, environmentally friendly products available.' },
    { icon: 'Users', title: 'Vetted professionals', description: 'All cleaners are background checked, trained and reviewed.' },
  ],
};

export { DEFAULT_WHY_CHOOSE_CONTENT };

/**
 * Homepage "Why choose us" section content from page_content, scoped to the
 * active org. Returns { content, usingDefault } so the component can resolve
 * the title dynamically against the org name.
 */
export function useWhyChooseContent(orgId: string | undefined) {
  return useQuery({
    queryKey: ['homepage-why-choose', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<{ content: WhyChooseContent; usingDefault: boolean }> => {
      const { data, error } = await supabase
        .from('page_content')
        .select('content')
        .eq('organization_id', orgId!)
        .eq('page', 'homepage')
        .eq('section', 'why_choose')
        .maybeSingle();

      if (error) throw error;

      if (data?.content) {
        return {
          content: data.content as unknown as WhyChooseContent,
          usingDefault: false,
        };
      }

      return { content: DEFAULT_WHY_CHOOSE_CONTENT, usingDefault: true };
    },
  });
}
